import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Construction, RefreshCw, KeyRound, LogOut } from 'lucide-react';
import { AuthContext } from '../App';
import { sanitizeHtml } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MaintenanceGuard = ({ children }) => {
  const { user, token, logout } = useContext(AuthContext);
  const [maintenance, setMaintenance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdminBypass, setShowAdminBypass] = useState(false);

  useEffect(() => {
    fetchMaintenanceStatus();
    
    // Poll every 30 seconds to check if maintenance is lifted
    const interval = setInterval(fetchMaintenanceStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchMaintenanceStatus = async () => {
    try {
      const response = await axios.get(`${API}/maintenance`);
      setMaintenance(response.data);
    } catch (error) {
      console.error('Failed to fetch maintenance status:', error);
      setMaintenance(null);
    } finally {
      setLoading(false);
    }
  };

  // Check if user is admin or super admin
  const isAdminOrSuperAdmin = user && (user.role === 'admin' || user.is_super_admin);

  // If maintenance is enabled and user is NOT admin/super admin, block access
  const shouldBlockAccess = maintenance?.enabled && !isAdminOrSuperAdmin && !showAdminBypass;

  // Allow bypass - hide banner to show login form (for admins to log in)
  const handleAdminBypass = () => {
    setShowAdminBypass(true);
  };

  // Handle logout for non-admin users during maintenance
  const handleLogoutDuringMaintenance = () => {
    logout();
    setShowAdminBypass(true); // Show login form after logout
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#f4911c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Indlæser...</p>
        </div>
      </div>
    );
  }

  // If maintenance is off or user is admin, show normal content
  if (!shouldBlockAccess) {
    return children;
  }

  // Show maintenance block screen
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="max-w-lg w-full mx-4 animate-in fade-in zoom-in duration-300">
        {/* Maintenance Card */}
        <div className="bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 p-1 rounded-2xl shadow-2xl">
          <div className="bg-white rounded-xl p-8">
            {/* Animated Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                {/* Ping animation */}
                <div className="absolute inset-0 bg-yellow-400 rounded-full animate-ping opacity-30"></div>
                <div className="absolute inset-0 bg-orange-400 rounded-full animate-pulse opacity-20"></div>
                {/* Main icon container */}
                <div className="relative bg-gradient-to-br from-yellow-100 to-orange-100 p-6 rounded-full border-4 border-yellow-300 shadow-lg">
                  <Construction className="w-16 h-16 text-yellow-600" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-center text-neutral-800 mb-4">
              Systemet er under vedligeholdelse
            </h1>

            {/* Message Content */}
            <div 
              className="prose prose-sm max-w-none text-center mb-6"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(maintenance.message) }}
            />

            {/* Show different options based on login status */}
            <div className="flex flex-col items-center space-y-3">
              {user ? (
                // User is logged in but not admin - offer to log out
                <>
                  <p className="text-sm text-neutral-500 text-center">
                    Du er logget ind som <strong>{user.first_name} {user.last_name}</strong>, 
                    men kun administratorer har adgang under vedligeholdelse.
                  </p>
                  <button
                    onClick={handleLogoutDuringMaintenance}
                    className="flex items-center space-x-2 px-6 py-3 bg-neutral-600 text-white font-medium rounded-lg hover:bg-neutral-700 transition-all shadow-md"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Log ud</span>
                  </button>
                </>
              ) : (
                // User is not logged in - offer to retry
                <button
                  onClick={() => window.location.reload()}
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-medium rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all shadow-md hover:shadow-lg"
                >
                  <RefreshCw className="w-5 h-5" />
                  <span>Prøv igen</span>
                </button>
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-neutral-200">
              <div className="flex items-center justify-center space-x-2 text-neutral-400 text-sm">
                <Construction className="w-4 h-4" />
                <span>Rock Under Broen - RUBCREW.DK</span>
              </div>
              {maintenance.updated_at && (
                <p className="text-xs text-neutral-400 text-center mt-2">
                  Opdateret: {new Date(maintenance.updated_at).toLocaleString('da-DK', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Warning stripes at bottom */}
        <div className="mt-4 h-4 bg-[repeating-linear-gradient(45deg,#f59e0b,#f59e0b_10px,#1f2937_10px,#1f2937_20px)] rounded-full opacity-80"></div>
        
        {/* Admin Bypass Link - subtle but accessible */}
        <div className="mt-4 text-center">
          <button
            onClick={handleAdminBypass}
            className="inline-flex items-center space-x-2 text-neutral-400 hover:text-white text-sm transition-colors group"
          >
            <KeyRound className="w-4 h-4 group-hover:text-yellow-400" />
            <span>Administrator login</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceGuard;
